import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api, { type InventoryItem } from '../service/api';
import { createPersistStorage } from './utils/persist';

interface InventoryState {
  inventory: InventoryItem[];
  pendingActivations: InventoryItem[];
  loading: boolean;
  error: string | null;
}

interface InventoryActions {
  refreshInventory: () => Promise<void>;
  refreshPendingActivations: () => Promise<void>;
  activateItem: (inventoryItemId: string, quantity?: number) => Promise<void>;
  processActivation: (
    inventoryItemId: string,
    action: 'approve' | 'deny',
  ) => Promise<void>;
}

type InventoryStore = InventoryState & InventoryActions;

type InventoryStorePersist = Pick<
  InventoryStore,
  'inventory' | 'pendingActivations'
>;

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

const useInventoryStore = create<InventoryStore>()(
  persist<InventoryStore, [], [], InventoryStorePersist>(
    (set, get) => ({
      inventory: [],
      pendingActivations: [],
      loading: false,
      error: null,

      refreshInventory: async () => {
        const state = get();
        const hasCachedData =
          state.inventory.length > 0 || state.pendingActivations.length > 0;
        set({ loading: !hasCachedData, error: null });
        try {
          const response = await api.inventoryGetAll();
          set({ inventory: response, loading: false });
        } catch (error) {
          set({
            error: getErrorMessage(error, 'Failed to load inventory'),
            loading: false,
          });
        }
      },

      refreshPendingActivations: async () => {
        const state = get();
        const hasCachedData =
          state.inventory.length > 0 || state.pendingActivations.length > 0;
        set({ loading: !hasCachedData, error: null });
        try {
          const response = await api.inventoryGetPendingActivations();
          set({ pendingActivations: response, loading: false });
        } catch (error) {
          set({
            error: getErrorMessage(
              error,
              'Failed to load pending activations',
            ),
            loading: false,
          });
        }
      },

      activateItem: async (inventoryItemId: string, quantity = 1) => {
        set({ loading: true, error: null });
        try {
          const request: { inventoryItemId: string; amount?: number } = {
            inventoryItemId,
          };
          if (quantity > 1) {
            request.amount = quantity;
          }

          await api.inventoryActivateItem(request);
          await get().refreshInventory();
        } catch (error) {
          set({
            error: getErrorMessage(error, 'Failed to activate item'),
            loading: false,
          });
        }
      },

      processActivation: async (
        inventoryItemId: string,
        action: 'approve' | 'deny',
      ) => {
        set({ loading: true, error: null });
        try {
          await api.inventoryProcessActivation(inventoryItemId, { action });
          await Promise.all([
            get().refreshInventory(),
            get().refreshPendingActivations(),
          ]);
        } catch (error) {
          set({
            error: getErrorMessage(error, 'Failed to process activation'),
            loading: false,
          });
        }
      },
    }),
    {
      name: 'inventory-store',
      storage: createPersistStorage<InventoryStorePersist>(),
      partialize: state => ({
        inventory: state.inventory,
        pendingActivations: state.pendingActivations,
      }),
    },
  ),
);

export const useInventory = () => useInventoryStore();

export default useInventoryStore;
